import { ApiClientResponse, ErrorResponse, RequestOptions } from './types';

const getRegionFromToken = (token: string): string => token.split(':')[0];

const getHeaders = (
  token?: string,
  additionalHeaders: Record<string, string> = {},
  useRegionHeader: boolean = true
): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    if (useRegionHeader) {
      headers['x-region'] = getRegionFromToken(token);
    }
  }

  return headers;
};

const request = async <T, E>(
  baseUrl: string,
  path: string,
  { method = 'GET', headers = {}, body, token, useRegionHeader = true }: RequestOptions<any> = {}
): Promise<ApiClientResponse<T, E>> => {
  try {
    let url = baseUrl;
    if (path.startsWith('?')) {
      url = `${baseUrl}${path}`;
    } else if (path.length) {
      url = `${baseUrl}/${path}`;
    }

    const options: RequestInit = {
      method,
      headers: getHeaders(token, headers, useRegionHeader),
      body: body ? JSON.stringify(body) : undefined,
    };

    const response = await fetch(url, options);
    const contentType = response.headers.get('Content-Type') || '';

    let result: any;
    if (contentType.includes('application/json')) {
      result = await response.json().catch(() => ({}));
    } else if (contentType.includes('text/html')) {
      const text = await response.text();
      try {
        result = JSON.parse(text); // Try to parse as JSON
      } catch {
        result = text; // If parsing fails, return as plain text
      }
    } else {
      result = await response.blob(); // Fallback for unknown content types
    }

    if (!response.ok) {
      return { response, error: { ...result, status: response.status } };
    }

    return { response, result };
  } catch (error) {
    return { response: undefined as any, error };
  }
};

export const createApiClient = (baseUrl: string) => ({
  get: <T, E = ErrorResponse>(
    path: string,
    token?: string,
    headers?: Record<string, string>,
    useRegionHeader: boolean = true
  ) => request<T, E>(baseUrl, path, { method: 'GET', token, headers, useRegionHeader }),
  post: <T, E = ErrorResponse>(path: string, body: any, token?: string) =>
    request<T, E>(baseUrl, path, { method: 'POST', body, token }),
  patch: <T, E = ErrorResponse>(path: string, body: any, token?: string) =>
    request<T, E>(baseUrl, path, { method: 'PATCH', body, token }),
  put: <T, E = ErrorResponse>(path: string, body: any, token?: string) =>
    request<T, E>(baseUrl, path, { method: 'PUT', body, token }),
  delete: <T, E = ErrorResponse>(path: string, body: any, token?: string) =>
    request<T, E>(baseUrl, path, { method: 'DELETE', body, token }),
});
